import type {
  MyChronChannelMeta,
  MyChronGpsPoint,
  MyChronSeriesSample,
  MyChronSessionExport,
} from './telemetrySession';

const EARTH_RADIUS_M = 6_371_000;
const GRAVITY_MPS2 = 9.80665;
const KART_WHEELBASE_M = 1.05;
const CURVATURE_WINDOW = 4;
const MAX_DERIVED_SAMPLES = 4000;
const MIN_SPEED_MPS = 2;
const MIN_WINDOW_DISTANCE_M = 4;

function hasBackwardsTimecode(samples: Array<{ timecodeMs: number }>) {
  return samples.some((sample, index) => index > 0 && sample.timecodeMs < samples[index - 1]!.timecodeMs);
}

function medianPositiveStepMs(samples: Array<{ timecodeMs: number }>) {
  const steps: number[] = [];
  for (let index = 1; index < samples.length; index += 1) {
    const step = samples[index]!.timecodeMs - samples[index - 1]!.timecodeMs;
    if (step > 0 && step <= 1_000) {
      steps.push(step);
    }
  }
  steps.sort((left, right) => left - right);
  return steps[Math.floor(steps.length / 2)] ?? 40;
}

function stitchResetTimecodes<T extends { timecodeMs: number }>(samples: T[]): T[] {
  if (!hasBackwardsTimecode(samples)) {
    return samples;
  }

  const sampleStepMs = medianPositiveStepMs(samples);
  let offsetMs = 0;
  let previousRawMs = samples[0]?.timecodeMs ?? 0;
  let previousTimecodeMs = previousRawMs;

  return samples.map((sample, index) => {
    if (index > 0 && sample.timecodeMs < previousRawMs) {
      offsetMs = previousTimecodeMs + sampleStepMs - sample.timecodeMs;
    }
    previousRawMs = sample.timecodeMs;
    previousTimecodeMs = sample.timecodeMs + offsetMs;
    return offsetMs === 0 ? sample : { ...sample, timecodeMs: previousTimecodeMs };
  });
}

/** Repair legacy Alfano compact-GPS exports whose internal run clocks restart at zero. */
export function withMonotonicAlfanoTimecodes(session: MyChronSessionExport): MyChronSessionExport {
  if (session.format !== 'alfano' || session.laps.length > 0 || !hasBackwardsTimecode(session.gps)) {
    return session;
  }

  return {
    ...session,
    gps: stitchResetTimecodes(session.gps),
    series: Object.fromEntries(
      Object.entries(session.series).map(([key, samples]) => [key, stitchResetTimecodes(samples)]),
    ),
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function bearingRad(from: MyChronGpsPoint, to: MyChronGpsPoint) {
  const fromLat = from.lat * Math.PI / 180;
  const toLat = to.lat * Math.PI / 180;
  const deltaLon = (to.lon - from.lon) * Math.PI / 180;
  return Math.atan2(
    Math.sin(deltaLon) * Math.cos(toLat),
    Math.cos(fromLat) * Math.sin(toLat)
      - Math.sin(fromLat) * Math.cos(toLat) * Math.cos(deltaLon),
  );
}

function distanceM(from: MyChronGpsPoint, to: MyChronGpsPoint) {
  const deltaLat = (to.lat - from.lat) * Math.PI / 180;
  const deltaLon = (to.lon - from.lon) * Math.PI / 180;
  const fromLat = from.lat * Math.PI / 180;
  const toLat = to.lat * Math.PI / 180;
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function normalizedAngleRad(value: number) {
  return Math.atan2(Math.sin(value), Math.cos(value));
}

export function deriveGpsSteeringAndLateralG(gps: MyChronGpsPoint[]): {
  steering: MyChronSeriesSample[];
  accY: MyChronSeriesSample[];
} {
  if (gps.length < 3) {
    return { steering: [], accY: [] };
  }

  const stride = Math.max(1, Math.ceil(gps.length / MAX_DERIVED_SAMPLES));
  const steering: MyChronSeriesSample[] = [];
  const accY: MyChronSeriesSample[] = [];

  for (let index = 0; index < gps.length; index += stride) {
    const point = gps[index]!;
    const before = gps[Math.max(0, index - CURVATURE_WINDOW)]!;
    const after = gps[Math.min(gps.length - 1, index + CURVATURE_WINDOW)]!;
    const incomingDistance = distanceM(before, point);
    const outgoingDistance = distanceM(point, after);
    const speedMps = Number.isFinite(point.speed) ? Math.max(0, point.speed) : 0;

    let steeringDeg = 0;
    let lateralG = 0;
    if (
      before !== point
      && after !== point
      && speedMps >= MIN_SPEED_MPS
      && incomingDistance + outgoingDistance >= MIN_WINDOW_DISTANCE_M
    ) {
      const headingChange = normalizedAngleRad(bearingRad(point, after) - bearingRad(before, point));
      const curvature = headingChange / Math.max((incomingDistance + outgoingDistance) / 2, 0.1);
      steeringDeg = clamp(Math.atan(KART_WHEELBASE_M * curvature) * 180 / Math.PI, -90, 90);
      lateralG = clamp(speedMps * speedMps * curvature / GRAVITY_MPS2, -3, 3);
    }

    steering.push({ timecodeMs: point.timecodeMs, value: steeringDeg });
    accY.push({ timecodeMs: point.timecodeMs, value: -lateralG });
  }

  return { steering, accY };
}

function derivedChannel(index: number, shortName: 'StrA' | 'AccY'): MyChronChannelMeta {
  return shortName === 'StrA'
    ? { index, shortName, longName: 'GPS-derived steering', unit: 'deg', decimals: 1 }
    : { index, shortName, longName: 'GPS-derived lateral acceleration', unit: 'G', decimals: 2 };
}

export function withGpsDerivedTelemetry(session: MyChronSessionExport): MyChronSessionExport {
  session = withMonotonicAlfanoTimecodes(session);
  const needsSteering = !session.series.StrA?.length;
  const needsLateralG = !session.series.AccY?.length;
  if ((!needsSteering && !needsLateralG) || session.gps.length < 3) {
    return session;
  }

  const derived = deriveGpsSteeringAndLateralG(session.gps);
  const channels = [...session.channels];
  if (needsSteering && !channels.some((channel) => channel.shortName === 'StrA')) {
    channels.push(derivedChannel(channels.length, 'StrA'));
  }
  if (needsLateralG && !channels.some((channel) => channel.shortName === 'AccY')) {
    channels.push(derivedChannel(channels.length, 'AccY'));
  }

  return {
    ...session,
    channels,
    series: {
      ...session.series,
      ...(needsSteering ? { StrA: derived.steering } : {}),
      ...(needsLateralG ? { AccY: derived.accY } : {}),
    },
  };
}
